<script setup lang="ts">
definePageMeta({
  layout: 'dashboard',
  middleware: ['username-required']
})

useSeoMeta({
  title: 'Friends',
  description: 'Manage your friends and friend requests'
})

const authStore = useAuthStore()
const friendsStore = useFriendsStore()
const notificationsStore = useNotificationsStore()
const toast = useToast()

const route = useRoute()
const loading = ref(true)
const activeTab = ref<'friends' | 'requests'>(route.query.tab === 'requests' ? 'requests' : 'friends')
const sendingRequest = ref(false)
const actionLoading = ref<string | null>(null)
const selectedUsername = ref<string | null>(null)
const characterSearchRef = ref<{ clearSelection: () => void } | null>(null)

const friends = computed(() => friendsStore.friends)
const incomingRequests = computed(() => friendsStore.incomingRequests)
const outgoingRequests = computed(() => friendsStore.outgoingRequests)

const friendLimit = computed(() => authStore.getPlanLimit('friends'))
const friendLimitLabel = computed(() => friendLimit.value === Infinity ? '∞' : String(friendLimit.value))
const friendCount = computed(() => friendsStore.friendCount)
const atLimit = computed(() => friendCount.value >= friendLimit.value)
const incomingCount = computed(() => friendsStore.incomingCount)
const outgoingCount = computed(() => friendsStore.outgoingCount)

function onCharacterSelect(character: { id: string, username: string }) {
  selectedUsername.value = character.username
}

async function handleSendRequest(username: string) {
  if (!username || !authStore.userId) return
  sendingRequest.value = true
  try {
    await friendsStore.sendRequest(authStore.userId, username)
    toast.add({
      title: 'Friend request sent!',
      description: `Request sent to @${username}`,
      icon: 'i-lucide-user-plus',
      color: 'success'
    })
    selectedUsername.value = null
    characterSearchRef.value?.clearSelection()
  } catch (err: any) {
    toast.add({
      title: 'Could not send request',
      description: err.data?.message || 'Something went wrong',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    sendingRequest.value = false
  }
}

async function handleAccept(friendshipId: string) {
  if (!authStore.userId) return
  actionLoading.value = friendshipId
  try {
    await friendsStore.acceptRequest(authStore.userId, friendshipId)
    notificationsStore.fetchUnreadCount(authStore.userId)
    toast.add({ title: 'Friend request accepted!', icon: 'i-lucide-check', color: 'success' })
  } catch (err: any) {
    toast.add({ title: 'Failed to accept', description: err.data?.message, icon: 'i-lucide-x', color: 'error' })
  } finally {
    actionLoading.value = null
  }
}

async function handleRemove(friendshipId: string) {
  if (!authStore.userId) return
  actionLoading.value = friendshipId
  try {
    await friendsStore.removeFriend(authStore.userId, friendshipId)
    toast.add({ title: 'Friend removed', icon: 'i-lucide-user-minus', color: 'success' })
  } catch (err: any) {
    toast.add({ title: 'Failed to remove', description: err.data?.message, icon: 'i-lucide-x', color: 'error' })
  } finally {
    actionLoading.value = null
  }
}

async function handleDeclineOrCancel(friendshipId: string) {
  if (!authStore.userId) return
  actionLoading.value = friendshipId
  try {
    await friendsStore.declineOrCancel(authStore.userId, friendshipId)
    notificationsStore.fetchUnreadCount(authStore.userId)
  } catch {
    // Silently fail
  } finally {
    actionLoading.value = null
  }
}

onMounted(async () => {
  if (authStore.userId) {
    await Promise.all([
      friendsStore.fetchFriends(authStore.userId, true),
      friendsStore.fetchRequests(authStore.userId)
    ])
  }
  loading.value = false
})
</script>

<template>
  <UDashboardPanel id="friends">
    <template #header>
      <UDashboardNavbar>
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #title>
          <UIcon
            name="i-lucide-users"
            class="size-5 text-primary-400"
          />
          <span>Friends</span>
        </template>

        <template #right>
          <ColorModeToggle />
          <NotificationBell />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="space-y-6">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex items-center justify-center py-12"
        >
          <UIcon
            name="i-lucide-loader-2"
            class="size-6 animate-spin text-muted"
          />
        </div>

        <template v-else>
          <!-- Stat cards -->
          <div class="grid grid-cols-2 gap-4 max-w-sm">
            <div class="rounded-2xl bg-elevated/50 border border-default p-5">
              <div class="flex items-center gap-3 mb-3">
                <div class="flex items-center justify-center size-8 rounded-lg bg-primary-500/10 ring-1 ring-inset ring-primary-500/25">
                  <UIcon
                    name="i-lucide-users"
                    class="size-4 text-primary-400"
                  />
                </div>
              </div>
              <span class="text-2xl font-bold text-highlighted">{{ friendCount }}</span>
              <p class="text-xs text-dimmed uppercase tracking-wide mt-1">
                Friends
              </p>
            </div>
            <div class="rounded-2xl bg-elevated/50 border border-default p-5">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center justify-center size-8 rounded-lg bg-elevated/50 ring-1 ring-inset ring-accented">
                  <UIcon
                    name="i-lucide-gauge"
                    class="size-4 text-dimmed"
                  />
                </div>
                <UTooltip
                  v-if="!authStore.isElite"
                  text="Upgrade for a higher friend limit"
                >
                  <UButton
                    icon="i-lucide-arrow-up-right"
                    size="xs"
                    color="primary"
                    variant="ghost"
                    to="/dashboard/settings/billing"
                  />
                </UTooltip>
              </div>
              <span class="text-2xl font-bold text-highlighted">{{ friendLimitLabel }}</span>
              <p class="text-xs text-dimmed uppercase tracking-wide mt-1">
                Limit
                <span
                  v-if="!authStore.isElite"
                  class="text-primary-400/70 normal-case tracking-normal cursor-pointer hover:text-primary-300"
                  @click="$router.push('/dashboard/settings/billing')"
                >
                  &middot; Upgrade
                </span>
              </p>
            </div>
          </div>

          <!-- Add friend search -->
          <div>
            <template v-if="atLimit">
              <div class="flex items-center gap-2 rounded-lg bg-elevated/50 border border-default px-4 py-3">
                <UIcon
                  name="i-lucide-lock"
                  class="size-4 text-dimmed shrink-0"
                />
                <p class="text-sm text-dimmed">
                  Friend limit reached ({{ friendLimit }}).
                  <NuxtLink
                    to="/dashboard/settings/billing"
                    class="text-primary-400 underline underline-offset-2 hover:text-primary-300"
                  >
                    Upgrade
                  </NuxtLink> to add more.
                </p>
              </div>
            </template>
            <div
              v-else
              class="flex items-stretch gap-2"
            >
              <CharacterSearch
                ref="characterSearchRef"
                placeholder="Search for a username to add..."
                class="flex-1"
                @select="onCharacterSelect"
                @clear="selectedUsername = null"
              />
              <UButton
                v-if="selectedUsername"
                icon="i-lucide-user-plus"
                label="Send Request"
                color="primary"
                class="self-stretch"
                :loading="sendingRequest"
                @click="handleSendRequest(selectedUsername!)"
              />
            </div>
          </div>

          <!-- Tab toggle -->
          <div class="flex items-center gap-1 p-1 rounded-xl bg-elevated/50 border border-default w-fit">
            <button
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2"
              :class="activeTab === 'friends'
                ? 'bg-elevated text-highlighted shadow-sm'
                : 'text-muted hover:text-toned'"
              @click="activeTab = 'friends'"
            >
              Friends
              <span class="text-xs text-dimmed">
                {{ friendCount }}/{{ friendLimitLabel }}
              </span>
            </button>
            <button
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2"
              :class="activeTab === 'requests'
                ? 'bg-elevated text-highlighted shadow-sm'
                : 'text-muted hover:text-toned'"
              @click="activeTab = 'requests'"
            >
              Requests
              <UTooltip
                v-if="incomingCount > 0"
                :text="`${incomingCount} incoming`"
              >
                <UBadge
                  color="primary"
                  variant="solid"
                  size="xs"
                  icon="i-lucide-arrow-down"
                  class="gap-0.5"
                >
                  {{ incomingCount }}
                </UBadge>
              </UTooltip>
              <UTooltip
                v-if="outgoingCount > 0"
                :text="`${outgoingCount} outgoing`"
              >
                <UBadge
                  color="neutral"
                  variant="subtle"
                  size="xs"
                  icon="i-lucide-arrow-up"
                  class="gap-0.5"
                >
                  {{ outgoingCount }}
                </UBadge>
              </UTooltip>
            </button>
          </div>

          <!-- Friends tab -->
          <template v-if="activeTab === 'friends'">
            <div
              v-if="friends.length === 0"
              class="flex flex-col items-center gap-3 py-12 text-center"
            >
              <UIcon
                name="i-lucide-users"
                class="size-12 text-dimmed"
              />
              <p class="text-sm text-dimmed">
                No friends yet. Search for a username above to send a friend request.
              </p>
            </div>

            <div
              v-else
              class="grid gap-3"
            >
              <div
                v-for="friend in friends"
                :key="friend.friendship_id"
                class="flex items-center gap-3 rounded-xl bg-elevated/50 border border-default p-4"
              >
                <div class="size-10 rounded-full overflow-hidden bg-elevated/50 ring-1 ring-accented shrink-0">
                  <img
                    v-if="friend.avatar_url"
                    :src="friend.avatar_url"
                    :alt="friend.username"
                    class="size-full object-cover"
                  >
                  <div
                    v-else
                    class="size-full flex items-center justify-center text-dimmed"
                  >
                    <UIcon
                      name="i-lucide-user"
                      class="size-5"
                    />
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-highlighted truncate">
                    @{{ friend.username }}
                  </p>
                </div>
                <div class="flex items-center gap-1.5">
                  <UTooltip text="View profile">
                    <UButton
                      icon="i-lucide-external-link"
                      size="xs"
                      color="neutral"
                      variant="ghost"
                      :to="`/${friend.username}`"
                      target="_blank"
                    />
                  </UTooltip>
                  <UTooltip text="Unfriend">
                    <UButton
                      icon="i-lucide-user-minus"
                      size="xs"
                      color="error"
                      variant="ghost"
                      :loading="actionLoading === friend.friendship_id"
                      @click="handleRemove(friend.friendship_id)"
                    />
                  </UTooltip>
                </div>
              </div>
            </div>
          </template>

          <!-- Requests tab -->
          <template v-else>
            <!-- Incoming -->
            <div v-if="incomingRequests.length > 0">
              <h3 class="text-xs font-medium text-dimmed uppercase tracking-wider mb-3">
                Incoming Requests
              </h3>
              <div class="grid gap-3">
                <div
                  v-for="req in incomingRequests"
                  :key="req.friendship_id"
                  class="flex items-center gap-3 rounded-xl bg-elevated/50 border border-default p-4"
                >
                  <NuxtLink
                    :to="`/${req.username}`"
                    class="shrink-0"
                  >
                    <div class="size-10 rounded-full overflow-hidden bg-elevated/50 ring-1 ring-accented">
                      <img
                        v-if="req.avatar_url"
                        :src="req.avatar_url"
                        :alt="req.username"
                        class="size-full object-cover"
                      >
                      <div
                        v-else
                        class="size-full flex items-center justify-center text-dimmed"
                      >
                        <UIcon
                          name="i-lucide-user"
                          class="size-5"
                        />
                      </div>
                    </div>
                  </NuxtLink>
                  <NuxtLink
                    :to="`/${req.username}`"
                    class="flex-1 min-w-0"
                  >
                    <p class="text-sm font-medium text-highlighted truncate">
                      @{{ req.username }}
                    </p>
                  </NuxtLink>
                  <div class="flex items-center gap-1.5">
                    <UButton
                      icon="i-lucide-check"
                      size="xs"
                      color="success"
                      variant="soft"
                      label="Accept"
                      :loading="actionLoading === req.friendship_id"
                      @click="handleAccept(req.friendship_id)"
                    />
                    <UTooltip text="Decline request">
                      <UButton
                        icon="i-lucide-x"
                        size="xs"
                        color="error"
                        variant="ghost"
                        :loading="actionLoading === req.friendship_id"
                        @click="handleDeclineOrCancel(req.friendship_id)"
                      />
                    </UTooltip>
                  </div>
                </div>
              </div>
            </div>

            <!-- Outgoing -->
            <div v-if="outgoingRequests.length > 0">
              <h3 class="text-xs font-medium text-dimmed uppercase tracking-wider mb-3">
                Outgoing Requests
              </h3>
              <div class="grid gap-3">
                <div
                  v-for="req in outgoingRequests"
                  :key="req.friendship_id"
                  class="flex items-center gap-3 rounded-xl bg-elevated/50 border border-default p-4"
                >
                  <NuxtLink
                    :to="`/${req.username}`"
                    class="shrink-0"
                  >
                    <div class="size-10 rounded-full overflow-hidden bg-elevated/50 ring-1 ring-accented">
                      <img
                        v-if="req.avatar_url"
                        :src="req.avatar_url"
                        :alt="req.username"
                        class="size-full object-cover"
                      >
                      <div
                        v-else
                        class="size-full flex items-center justify-center text-dimmed"
                      >
                        <UIcon
                          name="i-lucide-user"
                          class="size-5"
                        />
                      </div>
                    </div>
                  </NuxtLink>
                  <NuxtLink
                    :to="`/${req.username}`"
                    class="flex-1 min-w-0"
                  >
                    <p class="text-sm font-medium text-highlighted truncate">
                      @{{ req.username }}
                    </p>
                    <p class="text-xs text-dimmed">
                      Pending
                    </p>
                  </NuxtLink>
                  <UButton
                    icon="i-lucide-x"
                    label="Cancel"
                    size="xs"
                    color="neutral"
                    variant="ghost"
                    :loading="actionLoading === req.friendship_id"
                    @click="handleDeclineOrCancel(req.friendship_id)"
                  />
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div
              v-if="incomingRequests.length === 0 && outgoingRequests.length === 0"
              class="flex flex-col items-center gap-3 py-12 text-center"
            >
              <UIcon
                name="i-lucide-inbox"
                class="size-12 text-dimmed"
              />
              <p class="text-sm text-dimmed">
                No pending friend requests.
              </p>
            </div>
          </template>

          <!-- Limit warning -->
          <div
            v-if="friendCount >= friendLimit - 2 && friendCount < friendLimit"
            class="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3"
          >
            <UIcon
              name="i-lucide-alert-triangle"
              class="size-4 text-amber-400 shrink-0"
            />
            <p class="text-xs tint-amber">
              You're approaching your friend limit ({{ friendCount }}/{{ friendLimit }}).
              <NuxtLink
                to="/dashboard/settings/billing"
                class="underline underline-offset-2 hover:opacity-80"
              >
                Upgrade your plan
              </NuxtLink> to add more.
            </p>
          </div>
        </template>
      </div>
    </template>
  </UDashboardPanel>
</template>
